/**
 * Import Matrix — Issue #557
 * Grid visualization of import dependencies. Rows = importers, columns = imported files.
 * Shows patterns like high-fan-out utilities or heavily-imported modules.
 */

import { useMemo } from 'react';
import type { ImportGraphData, ImportGraphNode } from './importGraphFlowConverter';
import { ROLE_COLORS } from './importGraphFlowConverter';

interface ImportMatrixProps {
  data: ImportGraphData;
  onSelectFile?: (fileId: string) => void;
  // Lifted filter state from page (issue #566)
  searchQuery?: string;
  roleFilters?: Set<string>;
}

const MAX_MATRIX_SIZE = 60;

export function ImportMatrix({ data, onSelectFile, searchQuery = '', roleFilters = new Set() }: ImportMatrixProps) {
  // Filter nodes and limit for performance
  const nodes = useMemo((): ImportGraphNode[] => {
    let ns = [...data.graph.nodes];
    if (roleFilters.size > 0) ns = ns.filter(n => roleFilters.has(n.role));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      ns = ns.filter(n => n.name.toLowerCase().includes(q) || n.filePath.toLowerCase().includes(q));
    }
    // Sort by importedBy count desc to put most-used files first
    ns.sort((a, b) => b.importedBy.length - a.importedBy.length);
    return ns.slice(0, MAX_MATRIX_SIZE);
  }, [data.graph.nodes, roleFilters, searchQuery]);

  // Build edge set for O(1) lookup
  const edgeSet = useMemo(() => {
    const s = new Set<string>();
    for (const e of data.graph.edges) {
      s.add(`${e.source}|${e.target}`);
    }
    return s;
  }, [data.graph.edges]);

  const nodeIds = useMemo(() => new Set(nodes.map(n => n.id)), [nodes]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Count bar — filters are in page header (issue #566) */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
        <span className="text-sm text-gray-400">
          Showing {nodes.length} of {data.graph.nodes.length} files
          {nodes.length === MAX_MATRIX_SIZE && <span className="ml-1 text-amber-500">(limit {MAX_MATRIX_SIZE})</span>}
        </span>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#3b82f6', opacity: 0.8 }} />
          Imports (row → col)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-red-400 opacity-80" />
          Circular
        </span>
        <span className="text-gray-400">Rows = importers | Columns = imported</span>
      </div>

      {/* Matrix */}
      <div className="flex-1 overflow-auto">
        {nodes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">No files to display</div>
        ) : (
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-20 bg-gray-50 dark:bg-gray-800 w-40 min-w-40 px-2 py-1 border border-gray-200 dark:border-gray-700" />
                {nodes.map(col => (
                  <th
                    key={col.id}
                    className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-1"
                    title={col.filePath}
                  >
                    <div
                      className="w-4 h-24 flex items-end justify-center cursor-pointer"
                      onClick={() => onSelectFile?.(col.id)}
                    >
                      <span
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        style={{
                          writingMode: 'vertical-lr',
                          transform: 'rotate(180deg)',
                          whiteSpace: 'nowrap',
                          maxHeight: '90px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {col.name}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nodes.map(row => (
                <tr key={row.id}>
                  {/* Row label */}
                  <td
                    className="sticky left-0 z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => onSelectFile?.(row.id)}
                  >
                    <div className="flex items-center gap-1.5 max-w-36">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ROLE_COLORS[row.role] }} />
                      <span className="truncate text-gray-800 dark:text-gray-200">{row.name}</span>
                    </div>
                  </td>
                  {/* Cells */}
                  {nodes.map(col => {
                    if (row.id === col.id) {
                      // Diagonal
                      return (
                        <td
                          key={col.id}
                          className="border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
                          style={{ width: 20, height: 20 }}
                        />
                      );
                    }
                    const hasEdge = edgeSet.has(`${row.id}|${col.id}`);
                    const isCircular = hasEdge && edgeSet.has(`${col.id}|${row.id}`);
                    const bothVisible = nodeIds.has(row.id) && nodeIds.has(col.id);

                    return (
                      <td
                        key={col.id}
                        className={`border border-gray-200 dark:border-gray-700 ${hasEdge ? 'cursor-pointer' : ''}`}
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: hasEdge
                            ? isCircular
                              ? 'rgba(239,68,68,0.75)'
                              : (bothVisible ? 'rgba(59,130,246,0.75)' : 'rgba(59,130,246,0.4)')
                            : undefined,
                        }}
                        title={hasEdge ? `${row.name} imports ${col.name}${isCircular ? ' (circular)' : ''}` : undefined}
                        onClick={() => { if (hasEdge) onSelectFile?.(row.id); }}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
